import React, { useMemo, useState } from "react";
import { Button, PlainButton, Tooltip, Collapse } from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils/cx";
import { priceDisplay } from "@jugl-web/utils";
import { format } from "date-fns";
import { Plan } from "@jugl-web/rest-api";
import { SubscriptionIntervalType } from "@jugl-web/rest-api/entities/models/common-types/SubscriptionIntervalType";
import { ReactComponent as CheckIcon } from "./assets/check.svg";
import { ReactComponent as ChevronUpIcon } from "./assets/chevron-up.svg";
import gradientBackgroundFailed from "./assets/gradient-background-failed.png";

export interface PlanCardFeature {
  name: string;
  tooltip?: string;
}

export interface PlanCardProps {
  /** Pass plan + period to auto-derive planName, price, billingLabel, pricePeriodLabel */
  plan?: Plan;
  period?: SubscriptionIntervalType;
  planName?: string;
  billingLabel?: string;
  renewalLabel?: string;
  price?: number;
  pricePeriodLabel?: string;
  features: PlanCardFeature[];
  changePlanLabel?: string;
  onChangePlan?: () => void;
  defaultExpanded?: boolean;
  isChangeHidden?: boolean;
  hasPaymentFailed?: boolean;
  paymentFailedDescription?: React.ReactNode;
  isSubscriptionCanceled?: boolean;
  isSubscriptionPendingCancel?: boolean;
  cancelAt?: string | null;
  /** Overrides the default canceled-state description */
  canceledDescription?: React.ReactNode;
  /** Overrides the default canceled-state CTA label ("Renew Subscription") */
  canceledActionLabel?: string;
  onTryAgain?: () => void;
  onUpdatePaymentDetails?: () => void;
  onRenewSubscription?: () => void;
}

export const PlanCardInfo: React.FC<PlanCardProps> = ({
  plan,
  period,
  planName: planNameProp,
  billingLabel: billingLabelProp,
  renewalLabel: renewalLabelProp,
  price: priceProp,
  pricePeriodLabel: pricePeriodLabelProp,
  features,
  changePlanLabel = "Change Plan",
  onChangePlan,
  defaultExpanded = true,
  isChangeHidden = false,
  hasPaymentFailed = false,
  paymentFailedDescription,
  isSubscriptionCanceled = false,
  isSubscriptionPendingCancel = false,
  cancelAt,
  canceledDescription,
  canceledActionLabel,
  onTryAgain,
  onRenewSubscription,
}) => {
  const derived = useMemo(() => {
    if (!plan || !period) return null;
    const priceObj = plan.prices.find((p) => p.interval === period);
    const rawPrice = priceObj?.unit_amount ?? 0;
    const monthlyEquivalentPrice =
      period === "year" ? Math.round(rawPrice / 12 / 100) * 100 : rawPrice;
    return {
      planName: plan.name,
      price: monthlyEquivalentPrice,
      billingLabel: period === "year" ? "Yearly" : "Monthly",
      pricePeriodLabel: "per Month",
    };
  }, [plan, period]);

  const planName = planNameProp ?? derived?.planName ?? "Free";
  const billingLabel = billingLabelProp ?? derived?.billingLabel ?? "";
  const renewalLabel = renewalLabelProp ?? "";
  const price = priceProp ?? derived?.price ?? 0;
  const pricePeriodLabel =
    pricePeriodLabelProp ?? derived?.pricePeriodLabel ?? "";
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="overflow-hidden rounded-[30px] border border-solid border-dark-200 bg-white font-secondary">
      <div
        className="flex flex-col gap-10 px-8 py-7"
        style={{
          background:
            hasPaymentFailed ||
            isSubscriptionCanceled ||
            isSubscriptionPendingCancel
              ? `url(${gradientBackgroundFailed}) center/cover no-repeat, linear-gradient(180deg, #121315 0%, #5E1333 100%)`
              : `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><filter id="n" x="0" y="0" width="100%" height="100%"><feTurbulence type="fractalNoise" baseFrequency="12.5" numOctaves="20" stitchTiles="stitch"/></filter><rect width="100" height="100" filter="url(%23n)" opacity="0.20"/></svg>'),
              linear-gradient(180deg, #111116 0%, #273785 100%)`,
        }}
      >
        {!isSubscriptionCanceled && (
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1.5">
              <span className="text-[28px] font-semibold text-white">
                {planName}
              </span>
              <span className="text-sm leading-[1.15] text-white/90">
                {billingLabel}
                {renewalLabel && billingLabel && (
                  <>
                    {" "}
                    <span className="mx-2">•</span>
                  </>
                )}
                {renewalLabel}
              </span>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="text-[38px] font-semibold text-white">
                {priceDisplay(price, "usd")}
              </span>
              <span className="text-sm text-white/90">{pricePeriodLabel}</span>
            </div>
          </div>
        )}

        {isSubscriptionCanceled && (
          <div className="flex items-center justify-between gap-6">
            <div className="flex flex-col gap-2.5">
              <span className="font-secondary text-2xl font-semibold text-white">
                ⚠️ Subscription is Canceled
              </span>
              <p className="max-w-[600px] font-secondary text-sm leading-[1.5] text-[#F2F2F4]">
                {canceledDescription ??
                  "Plan & Add-ons features is not available anymore"}
              </p>
            </div>
            <PlainButton
              className="flex h-10 shrink-0 items-center gap-2.5 rounded-xl border border-solid border-[#A9BDFF] bg-white px-6 font-secondary text-[15px] font-medium text-dark capitalize"
              onClick={onRenewSubscription}
            >
              {canceledActionLabel ?? "Renew Subscription"}
              <span>→</span>
            </PlainButton>
          </div>
        )}

        {isSubscriptionPendingCancel && !isSubscriptionCanceled && (
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-2.5">
              <span className="font-secondary text-2xl font-semibold text-white">
                ⚠️ Cancels soon
              </span>
              <p className="max-w-[600px] font-secondary text-sm leading-[1.5] text-[#F2F2F4]">
                Plan & Add-ons features will be active till the end of the
                billing cycle
                {cancelAt &&
                  ` on ${format(new Date(cancelAt), "MMM dd, yyyy")}`}
                .
              </p>
            </div>
            <div>
              <PlainButton
                className="flex h-10 items-center gap-2.5 rounded-xl border border-solid border-[#A9BDFF] bg-white px-6 font-secondary text-[15px] font-medium text-dark capitalize"
                onClick={onRenewSubscription}
              >
                Reactivate Plan
                <span>→</span>
              </PlainButton>
            </div>
          </div>
        )}

        {hasPaymentFailed && !isSubscriptionCanceled && (
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-2.5">
              <span className="font-secondary text-2xl font-semibold text-white">
                ⚠️ Payment require additional action
              </span>
              {paymentFailedDescription && (
                <p className="max-w-[600px] font-secondary text-sm leading-[1.5] text-[#F2F2F4]">
                  {paymentFailedDescription}
                </p>
              )}
            </div>
            <div>
              <PlainButton
                className="flex h-10 items-center gap-2.5 rounded-xl border border-solid border-[#A9BDFF] bg-white px-6 font-secondary text-[15px] font-medium text-dark capitalize"
                onClick={onTryAgain}
              >
                Complete Payment
                <span>→</span>
              </PlainButton>
            </div>
          </div>
        )}
      </div>

      {(!isSubscriptionCanceled || isSubscriptionPendingCancel) && (
        <div className="px-8 py-6">
          <div className="flex items-center gap-3 pb-6">
            <PlainButton
              onClick={() => setIsExpanded((prev) => !prev)}
              className={cx(
                "transition-transform",
                isExpanded ? "rotate-180" : "rotate-0"
              )}
            >
              <ChevronUpIcon />
            </PlainButton>
            <span className="text-sm font-medium text-[#333333]">
              Plan details
            </span>
          </div>
          <Collapse isOpen={isExpanded}>
            <div className="grid grid-cols-2 gap-x-6 gap-y-[14px]">
              {features.map((feature) => (
                <Tooltip
                  isDisabled={!feature.tooltip}
                  key={feature.name}
                  renderTrigger={({ ref, props }) => {
                    return (
                      <div
                        ref={ref}
                        {...props}
                        className="flex min-h-[42px] items-center gap-3 text-dark-900"
                        key={feature.name}
                      >
                        <CheckIcon className="shrink-0" />
                        <span className="text-sm">{feature.name}</span>
                      </div>
                    );
                  }}
                >
                  {feature.tooltip}
                </Tooltip>
              ))}
            </div>
          </Collapse>

          {!isChangeHidden && (
            <Button
              variant="tertiary"
              className="mt-6 h-[32px] text-sm font-medium text-dark-900"
              onClick={onChangePlan}
            >
              {changePlanLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
